"use client"

import type React from "react"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Textarea } from "../../../components/ui/textarea"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { Facebook, Instagram, Youtube, Twitter, Share2, Search, Music2 } from "lucide-react"

const socialPlatforms = [
  { id: "facebook", name: "Facebook", icon: Facebook, placeholder: "https://web.facebook.com/profile.php?id=61556604022000" },
  { id: "instagram", name: "Instagram", icon: Instagram, placeholder: "https://www.instagram.com/titothewriter/" },
  { id: "TikTok", name: "TikTok", icon: Music2, placeholder: "https://www.tiktok.com/@titothewriter1" },
  { id: "youtube", name: "YouTube", icon: Youtube, placeholder: "https://www.youtube.com/@titothewriter" },
  { id: "twitter", name: "Twitter/X", icon: Twitter, placeholder: "https://twitter.com/titothewriter" },
]

export function SocialSEOSection({allUsers,adminUsers,comments}:any) {
  const [socialLinks, setSocialLinks] = useState({
    facebook: "https://web.facebook.com/profile.php?id=61556604022000",
    instagram: "https://www.instagram.com/titothewriter/",
    tiktok: "https://www.tiktok.com/@titothewriter1",
    youtube: "https://www.youtube.com/@titothewriter",
    twitter: "https://twitter.com/titothewriter",
  })

  const [seoSettings, setSeoSettings] = useState({
    metaTitle: "TSO Ministries - Till Every Eye Sees Jesus",
    metaDescription:
      "Known by many as Titoluwa Ministries, her ministry has impacted thousands through inspired writings, prophetic teachings, and global outreach campaigns.",
    ogImage: "https://tsoministries.com/wp-content/uploads/2025/06/IMG_2859-.jpg",
    favicon: "https://tsoministries.com/wp-content/uploads/2025/06/IMG_2859-.jpg",
  })

  const handleSocialLinkChange = (platform: string, value: string) => {
    setSocialLinks((prev) => ({ ...prev, [platform]: value }))
  }

  const handleSEOChange = (field: string, value: string) => {
    setSeoSettings((prev) => ({ ...prev, [field]: value }))
  }

  const saveSocialSettings = () => {
    console.log("Saving social settings:", socialLinks)
  }

  const saveSEOSettings = () => {
    console.log("Saving SEO settings:", seoSettings)
  }

  const handleImageUpload = (field: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const imageUrl = URL.createObjectURL(file)
      setSeoSettings((prev) => ({ ...prev, [field]: imageUrl }))
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Social Media & SEO Settings
          </CardTitle>
          <CardDescription>Manage your social media presence and search engine optimization</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="social" className="space-y-4">
            <TabsList>
              <TabsTrigger value="social">Social Media</TabsTrigger>
              <TabsTrigger value="seo">SEO Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="social" className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Social Media Links</h3>
                <p className="text-sm text-muted-foreground">
                  Add your social media profiles to display on your website
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                  {socialPlatforms.map((platform) => (
                    <div key={platform.id} className="space-y-2">
                      <Label htmlFor={platform.id} className="flex items-center gap-2">
                        <platform.icon className="h-4 w-4" />
                        {platform.name}
                      </Label>
                      <Input
                        id={platform.id}
                        value={socialLinks[platform.id as keyof typeof socialLinks]}
                        onChange={(e) => handleSocialLinkChange(platform.id, e.target.value)}
                        placeholder={platform.placeholder}
                      />
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <Button onClick={saveSocialSettings}>Save Social Media Settings</Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="seo" className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Search className="h-5 w-5" />
                  <h3 className="text-lg font-medium">SEO Configuration</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="meta-title">Meta Title</Label>
                    <Input
                      id="meta-title"
                      value={seoSettings.metaTitle}
                      onChange={(e) => handleSEOChange("metaTitle", e.target.value)}
                      placeholder="Your site title for search engines"
                      maxLength={60}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{seoSettings.metaTitle.length}/60 characters</p>
                  </div>

                  <div>
                    <Label htmlFor="meta-description">Meta Description</Label>
                    <Textarea
                      id="meta-description"
                      value={seoSettings.metaDescription}
                      onChange={(e) => handleSEOChange("metaDescription", e.target.value)}
                      placeholder="Brief description of your ministry for search results"
                      maxLength={160}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {seoSettings.metaDescription.length}/160 characters
                    </p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="og-image">Open Graph Image</Label>
                      <div className="space-y-2">
                        <Input
                          id="og-image"
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload("ogImage", e)}
                        />
                        {seoSettings.ogImage && (
                          <div className="border rounded p-2">
                            <img
                              src={seoSettings.ogImage || "/placeholder.svg"}
                              alt="OG Preview"
                              className="w-full h-24 object-cover rounded"
                            />
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">Recommended: 1200x630px</p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="favicon">Favicon</Label>
                      <div className="space-y-2">
                        <Input
                          id="favicon"
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload("favicon", e)}
                        />
                        {seoSettings.favicon && (
                          <div className="border rounded p-2 w-fit">
                            <img
                              src={seoSettings.favicon || "/placeholder.svg"}
                              alt="Favicon Preview"
                              className="w-8 h-8"
                            />
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">Recommended: 32x32px or 16x16px</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button onClick={saveSEOSettings}>Save SEO Settings</Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
